import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reverseUrl } from '../../../../../navigation/reverseUrl'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { useAuth } from '../../../../../hooks/useAuth'
import { useCurrentSociety } from '../../../../../hooks/useCurrentSociety'
import { ticketQueries } from '../../../../../api/ticket'
import { TicketListViewBoard, filter } from '../TicketListView/TicketListView'
import { Icon, IconChoices } from '../../../../../components/common/Icon'
import { DropdownSelect } from '../../../../../components/common/DropdownSelect/DropdownSelect'
import { societyQueries } from '../../../../../api/societies'
import { DropdownItemContent } from '../../../../../components/feed/DropdownItemContent/DropdownItemContent'
import { SocietyAvatar } from '../../../../../components/common/Avatar'
import { notificationsQueries } from '../../../../../api/notifications'
import { PillButton } from '../../../../../components/ticket/PillButton'
import { theme } from '../../../../../theme/theme'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../../../helpers/society'

export const SocietyManagementTicketSideBarContent = (): React.ReactNode => {
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const { userId } = useAuth()
  const { data: userSocieties } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const navigate = useNavigate()
  const [variant, setVariant] = useState<'my' | 'open' | 'all'>('my')

  const { data: notifications } = useQuery(
    notificationsQueries.getUserUnseenUnreadCount(userId)
  )

  const { data: myUnreadCount } = useQuery(
    ticketQueries.getUnreadTicketsCount(filter('my', userId!), society?._id)
  )
  const { data: openUnreadCount } = useQuery(
    ticketQueries.getUnreadTicketsCount(filter('open', userId!), society?._id)
  )

  const selectableSocieties = userSocieties?.filter(
    (_society) =>
      isUserBoardMemberInSociety(_society, userId as string) ||
      isUserAdminInSociety(_society, userId as string)
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col px-4 pt-6">
      <h2 style={theme.textVariants.h2} className="mb-4">
        {translate('ticketBoardView.title')}
      </h2>

      <div className="flex w-full justify-between items-center gap-2">
        <Link
          className="flex gap-2"
          to={reverseUrl('society:home', { id: society?._id })}
        >
          <Icon icon={IconChoices.ARROW_LEFT} />
        </Link>

        {(selectableSocieties?.length ?? 0) > 1 && (
          <DropdownSelect
            value={society?._id}
            options={
              selectableSocieties?.map((_society) => ({
                value: _society._id,
                label: _society.name,
                image: <SocietyAvatar society={_society} size={32} />,
                displayNotificationBadge:
                  !!notifications?.ticketsBoard[_society._id],
              })) || []
            }
            renderItemContent={(option) => (
              <DropdownItemContent option={option} />
            )}
            onChange={(value) =>
              navigate(reverseUrl('management:tickets', { id: value }))
            }
            dropdownWidth="w-full"
          />
        )}
        <Link
          className=""
          to={reverseUrl('management:ticket-create', { id: society?._id })}
        >
          <Icon icon={IconChoices.NEW_MESSAGE} />
        </Link>
      </div>
      <div className="flex gap-2 mt-4">
        <PillButton
          title={translate('ticketNav.my')}
          onClick={() => setVariant('my')}
          active={variant === 'my'}
          notificationCount={myUnreadCount}
        />
        <PillButton
          title={translate('ticketNav.open')}
          onClick={() => setVariant('open')}
          active={variant === 'open'}
          notificationCount={openUnreadCount}
        />
        <PillButton
          title={translate('ticketNav.all')}
          onClick={() => setVariant('all')}
          active={variant === 'all'}
        />
      </div>
      <TicketListViewBoard variant={variant} />
    </div>
  )
}
